import {Inject, Injectable} from '@angular/core';
import {TmsCollector, TmsCollectorConfig, WindowObject} from '@spartacus/tracking/tms/core';
import {CxEvent, EventService} from '@spartacus/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {DCP_EDDL_DATA_LAYER_SERVICE} from './tokens';
import {DcpDataService} from '../dcp-data.service';
import {isObservable} from 'rxjs';
import {filter, map, take} from 'rxjs/operators';
import {EddlInitializationEvent} from './events/eddl-initialization-event';
import {EddlMergedAbstract} from './events/eddl-merged.abstract';
import {DcpEddlDispatchService} from './dcp-eddl-dispatch-beacon.service';
import {DcpKymaService} from '@app/services/dcp-kyma.service';
import {HttpClient} from '@angular/common/http';
import {
  EddlAdobeTrackingData,
  EddlBeaconKey,
  EddlCollectionState
} from '@app/services/analytics/EDDL/models/eddl-adobe-tracking-data';
import {DpcEddlQueueService} from '@app/services/analytics/EDDL/dpc-eddl-queue.service';
import {DcpEddlSessionHelperService} from '@app/services/analytics/EDDL/dcp-eddl-session-helper.service';
import {EddlSearchResultsClickEvent} from './events/eddl-search-results-click-event';
import {EddlStBeaconEvent} from './events/eddl-stbeacon-event';
import {EddlStlBeaconEvent} from './events/eddl-stlbeacon-event';
import {DATA_LAYER_EVENT, IFrameMessageService} from '@app/services/iframe/iframe-message.service';
import {withWipeBuffer} from '@app/services/analytics/EDDL/eddl-wipe-buffer.util';

export const EDDL_DATA_LAYER_NAME = 'adobeDataLayer';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DcpEddlCollectorService implements TmsCollector {

  constructor(
    @Inject(DCP_EDDL_DATA_LAYER_SERVICE) private dataServiceList: DcpDataService<DcpEddlCollectorService>[],
    private dispatchService: DcpEddlDispatchService,
    private dpcEddlQueueService: DpcEddlQueueService,
    private dcpEddlSessionHelperService: DcpEddlSessionHelperService,
    private events: EventService,
    private kymaService: DcpKymaService,
    private http: HttpClient,
    private iFrameMessageService: IFrameMessageService,
  ) {

    this.dpcEddlQueueService
      .getQueue$()
      .pipe(
        untilDestroyed(this),
        map((q) =>
          q
            .filter((it) => it.event?.collectState === EddlCollectionState.FINISH)
            .map((it) => ({collectToken: it.event.collectToken})),
        ),
        filter((q) => q?.length > 0),
      )
      .subscribe((collectTokens) => {
        collectTokens.forEach(({collectToken}) => this.pushCollection(collectToken));
      });
  }

  init(config: TmsCollectorConfig, windowObject: WindowObject): void {
    windowObject[EDDL_DATA_LAYER_NAME] = windowObject[EDDL_DATA_LAYER_NAME] ?? [];
    setTimeout(() => {
      this.events.dispatch<EddlInitializationEvent>(new EddlInitializationEvent());
    });
  }

  pushEvent<T extends CxEvent>(config: TmsCollectorConfig, windowObject: WindowObject, event: T): void {
    this.triggerDataServices<T>(windowObject, event);
  }

  private pushCollection(collectToken: string) {
    let collectData = this.dpcEddlQueueService.getQueue();

    if (collectData.every((e) => e.event.collectOrder)) {
      collectData.sort((a, b) => a.event.collectOrder - b.event.collectOrder);
    }

    const queuedItems = this.dpcEddlQueueService.getQueue().filter((q) => q.event.collectToken === collectToken);

    queuedItems.forEach(({wo, event, data}) =>
      this.updateDataLayer<EddlMergedAbstract>(wo, data, event, {
        skipBeacon: event.collectState !== EddlCollectionState.FINISH,
      }),
    );
  }

  protected triggerDataServices<T>(windowObject: WindowObject, event: T): void {
    this.dataServiceList.forEach((dataService) => {
      const data = dataService.getData(event);
      if (isObservable(data)) {
        data.pipe(take(1), untilDestroyed(this)).subscribe((observableData) => {
          if ((event as any)?.collectToken && observableData) {
            this.dpcEddlQueueService.updateQueue({event, data: observableData, wo: windowObject});
          } else {
            this.updateDataLayer(windowObject, observableData, event);
          }
        });
      } else {
        if ((event as any)?.collectToken && data) {
          this.dpcEddlQueueService.updateQueue({event, data, wo: windowObject});
        } else {
          this.updateDataLayer(windowObject, data, event);
        }
      }
    });
  }

  protected updateDataLayer<T extends CxEvent & {manualBeacon?: EddlBeaconKey}>(
    windowObject: WindowObject,
    data: DcpEddlCollectorService,
    event: T,
    options?: {
      skipBeacon?: boolean;
      skipPush?: boolean;
      manually?: EddlBeaconKey;
    },
  ): void {
    if (data) {
      this.http
        .post<DcpEddlCollectorService>(this.kymaService.getUrl('eddlEvents'), data)
        .pipe(untilDestroyed(this), take(1))
        .subscribe({
          next: () => this.redirectToProductPage(event as EddlSearchResultsClickEvent),
          error: (err) => {
            console.error(err);
            this.redirectToProductPage(event as EddlSearchResultsClickEvent);
          },
        });

      // Guard against null windowObject in SSR/test environments
      if (!windowObject) {
        console.warn('[EDDL] WindowObject is null, skipping data layer push. This is expected in SSR environments.');
        return;
      }

      if (!options?.skipPush) {
        // Forward the UN-wiped payload to the hosting page so it can re-apply its own wipe
        // buffer against the outer adobeDataLayer (DCP-61998). The push below lands on the
        // iframe's own window, which is torn down shortly after add-to-cart, so without this
        // forward the event never reaches the hosting page. Scoped to window.location.origin so
        // the payload (user/account/page attributes) is only delivered to a same-origin parent and
        // never leaks to a cross-origin embedder; a no-op when not iframed (postMessage guards on
        // window.parent !== window). The /events/eddl POST above stays the authoritative path.
        if (typeof window !== 'undefined') {
          this.iFrameMessageService.postMessage({eventId: DATA_LAYER_EVENT, details: data}, window.location.origin);
        }

        // Prepend undefined values matching the current products array length so Adobe's
        // data layer overwrites old elements in the same push, preventing bleedover.
        const toPush = withWipeBuffer(
          data as EddlAdobeTrackingData,
          windowObject[EDDL_DATA_LAYER_NAME].getState?.() as {ecommerce?: {products?: unknown[]}}
        );
        windowObject[EDDL_DATA_LAYER_NAME].push(toPush);
      }

      // Beacon events don't trigger more beacons
      if (event instanceof EddlStBeaconEvent || event instanceof EddlStlBeaconEvent) {
        return;
      }

      if (!options?.skipBeacon) {
        if (event.hasOwnProperty('manualBeacon')) {
          this.dispatchService.triggerBeaconEventManually(event?.manualBeacon);
        } else {
          this.dispatchService.triggerBeaconEvents(event);
        }
        if (event.hasOwnProperty('collectToken')) {
          this.dcpEddlSessionHelperService.clearSessionHelper((event as EddlMergedAbstract).collectToken);
          this.dpcEddlQueueService.clearQueue((event as EddlMergedAbstract).collectToken);
        }
      }
    }
  }

  private redirectToProductPage(event: EddlSearchResultsClickEvent): void {
    // DCP-52439 - redirect to product page after eddl event sent
    if (event.eventSource === 'WebComponent' && event.productUrl) {
      // Use assign (not replace) so PLP stays in history and back returns to it with filters intact.
      // The href left-click is prevented in the tile component so this is the only navigation.
      window.location.assign((event as any).productUrl);
    }
  }
}
